import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Link, 
  Container, 
  Grid, 
  IconButton, 
  useTheme, 
  alpha, 
  Stack, 
  Divider, 
  Snackbar, 
  Alert, 
  useMediaQuery, 
  Button,
  Chip,
  Tooltip
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import TwitterIcon from '@mui/icons-material/Twitter';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CallIcon from '@mui/icons-material/Call';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import GetAppIcon from '@mui/icons-material/GetApp';
import StoreIcon from '@mui/icons-material/Store';
import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchRestaurantInfo } from './api';
import config from './config';

function Footer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [bgImage, setBgImage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const isRtl = i18n.dir() === 'rtl';

  // App URL constant
  const APP_DOWNLOAD_URL = 'https://storesye.com/app';
  const MOTHER_COMPANY_URL = 'https://codespace.storesye.com/';

  useEffect(() => {
    fetchRestaurantInfo().then(setRestaurantInfo).catch(console.error);

    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const image = data.find(img => img.Order === 1 && img.IsActive);
          if (image) {
            const imgPath = image.ImagePath.startsWith('http') 
              ? image.ImagePath 
              : `${config.SITE_BASE_URL}${image.ImagePath.startsWith('/') ? '' : '/'}${image.ImagePath}`;
            setBgImage(imgPath);
          }
        }
      })
      .catch(console.error);
  }, []);

  const additionalPhones = useMemo(() => {
    if (!restaurantInfo?.AdditionalPhones) return [];
    try {
      return typeof restaurantInfo.AdditionalPhones === 'string' 
        ? JSON.parse(restaurantInfo.AdditionalPhones) 
        : restaurantInfo.AdditionalPhones;
    } catch (e) {
      return [];
    }
  }, [restaurantInfo]);

  // Social media links configuration with conditional display
  const socialLinks = useMemo(() => {
    if (!restaurantInfo) return [];
    
    const links = [
      { 
        icon: <FacebookIcon />, 
        url: restaurantInfo.FacebookUrl, 
        name: 'Facebook',
        color: '#1877F2'
      },
      { 
        icon: <InstagramIcon />, 
        url: restaurantInfo.InstagramUrl, 
        name: 'Instagram',
        color: '#E4405F'
      },
      { 
        icon: <TwitterIcon />, 
        url: restaurantInfo.TwitterUrl, 
        name: 'Twitter',
        color: '#1DA1F2'
      }
    ];
    
    return links.filter(link => link.url && link.url.trim() !== '');
  }, [restaurantInfo]);

  const phoneIcons = [
    <PhoneIcon />,
    <WhatsAppIcon />,
    <CallIcon />,
    <SupportAgentIcon />,
    <HeadsetMicIcon />,
    <ContactPhoneIcon />
  ];

  const getPhoneIcon = (index) => phoneIcons[index % phoneIcons.length];

  const handleCopy = (text, label = '') => {
    if (text) {
      navigator.clipboard.writeText(text);
      setSnackbarMessage(label ? `${label} ${t('footer.copied', 'copied!')}` : t('footer.copiedToClipboard', 'Copied to clipboard!'));
      setSnackbarOpen(true);
    }
  };

  const handleWhatsApp = (phone) => {
    if (phone) {
      const cleanPhone = phone.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/${cleanPhone}`, '_blank');
    }
  };

  const logoUrl = restaurantInfo?.LogoPath 
    ? (restaurantInfo.LogoPath.startsWith('http') 
        ? restaurantInfo.LogoPath 
        : `${config.SITE_BASE_URL}${restaurantInfo.LogoPath.startsWith('/') ? '' : '/'}${restaurantInfo.LogoPath}`)
    : null;

  const SocialButton = ({ icon, href, name, color }) => (
    <Tooltip title={name} arrow>
      <IconButton 
        component="a" 
        href={href} 
        target="_blank"
        rel="noopener noreferrer"
        aria-label={name}
        size={isMobile ? "small" : "medium"}
        sx={{ 
          color: 'white', 
          bgcolor: alpha(color, 0.2),
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(color, 0.3)}`,
          width: { xs: 44, sm: 50 },
          height: { xs: 44, sm: 50 },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { 
            bgcolor: color, 
            color: 'white',
            transform: 'translateY(-6px) scale(1.1)',
            boxShadow: `0 8px 25px ${alpha(color, 0.5)}`,
            border: `1px solid ${color}`,
          },
          '&:active': {
            transform: isMobile ? 'scale(0.95)' : 'translateY(-2px) scale(1.05)'
          }
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: { xs: 22, sm: 24 } } })}
      </IconButton>
    </Tooltip>
  );

  const FooterLink = ({ href, children, icon }) => (
    <Link 
      href={href} 
      underline="none" 
      color={alpha('#fff', 0.7)} 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 1,
        transition: 'all 0.3s',
        fontSize: { xs: '0.9rem', sm: '0.95rem' },
        py: 0.5,
        '&:hover': { 
          color: theme.palette.primary.light,
          transform: isRtl ? 'translateX(-8px)' : 'translateX(8px)',
          '& .arrow-icon': {
            transform: isRtl ? 'translateX(-4px)' : 'translateX(4px)',
          }
        },
        '&:active': {
          transform: isMobile ? 'scale(0.98)' : (isRtl ? 'translateX(-8px)' : 'translateX(8px)')
        }
      }}
    >
      {icon && React.cloneElement(icon, { sx: { fontSize: { xs: 16, sm: 18 }, opacity: 0.8 } })}
      {children}
      <KeyboardArrowRightIcon 
        className="arrow-icon"
        sx={{ 
          fontSize: { xs: 16, sm: 18 }, 
          ml: 'auto',
          transform: isRtl ? 'rotate(180deg)' : 'none',
          opacity: 0.5,
          transition: 'transform 0.3s'
        }} 
      />
    </Link>
  );

  const ContactItem = ({ icon, label, text, href, onClick, copyable }) => (
    <Box sx={{ 
      display: 'flex', 
      gap: { xs: 1.5, sm: 2 }, 
      alignItems: 'flex-start',
      p: { xs: 1.5, sm: 2 },
      borderRadius: 2,
      bgcolor: alpha('#fff', 0.03),
      border: `1px solid ${alpha('#fff', 0.08)}`,
      transition: 'all 0.3s',
      '&:hover': {
        bgcolor: alpha('#fff', 0.06),
        borderColor: alpha(theme.palette.primary.light, 0.3),
        transform: 'translateY(-2px)'
      }
    }}>
      <Box sx={{ 
        color: theme.palette.primary.light, 
        bgcolor: alpha(theme.palette.primary.main, 0.25),
        p: { xs: 1, sm: 1.2 },
        borderRadius: '12px',
        display: 'flex',
        flexShrink: 0,
        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
      }}>
        {React.cloneElement(icon, { sx: { fontSize: { xs: 18, sm: 22 } } })}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {label && (
          <Typography variant="caption" sx={{ 
            color: alpha('#fff', 0.5), 
            display: 'block', 
            mb: 0.5,
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}>
            {label}
          </Typography>
        )}
        {href ? (
          <Link 
            href={href} 
            target={href.startsWith('http') ? '_blank' : undefined}
            rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
            color="inherit" 
            underline="hover" 
            sx={{ 
              color: alpha('#fff', 0.95), 
              '&:hover': { color: 'white' },
              fontSize: { xs: '0.85rem', sm: '0.95rem' },
              fontWeight: 500
            }}
          >
            {text}
          </Link>
        ) : onClick ? (
          <Typography 
            variant="body2" 
            onClick={onClick}
            sx={{ 
              color: alpha('#fff', 0.95), 
              lineHeight: 1.6, 
              fontSize: { xs: '0.85rem', sm: '0.95rem' }, 
              wordBreak: 'break-word',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': { color: theme.palette.primary.light }
            }}
          >
            {text}
          </Typography>
        ) : (
          <Typography 
            variant="body2" 
            onClick={copyable ? () => handleCopy(text, label) : undefined}
            sx={{ 
              color: alpha('#fff', 0.95), 
              lineHeight: 1.6, 
              fontSize: { xs: '0.85rem', sm: '0.95rem' }, 
              wordBreak: 'break-word',
              cursor: copyable ? 'pointer' : 'default',
              fontWeight: 500,
              '&:hover': copyable ? { color: theme.palette.primary.light } : {}
            }}
          >
            {text}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const SectionTitle = ({ children, icon }) => (
    <Box sx={{ mb: { xs: 2.5, sm: 3.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {icon && React.cloneElement(icon, { 
          sx: { fontSize: { xs: 22, sm: 26 }, color: theme.palette.primary.light } 
        })}
        <Typography variant="h6" sx={{ 
          fontWeight: 800, 
          fontSize: { xs: '1.15rem', sm: '1.35rem' },
          background: `linear-gradient(135deg, #fff 0%, ${theme.palette.primary.light} 100%)`,
          backgroundClip: 'text',
          textFillColor: 'transparent',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          {children}
        </Typography>
      </Box>
      <Box sx={{ 
        width: { xs: 40, sm: 50 }, 
        height: { xs: 3, sm: 4 }, 
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
        borderRadius: 2
      }} />
    </Box>
  );

  return (
    <Box sx={{
      position: 'relative',
      color: 'white',
      pt: { xs: 8, sm: 10, md: 12 },
      pb: { xs: 4, sm: 5 },
      mt: 'auto',
      borderTopLeftRadius: { xs: 30, sm: 40, md: 60 },
      borderTopRightRadius: { xs: 30, sm: 40, md: 60 },
      overflow: 'hidden',
      backgroundImage: bgImage ? `url(${bgImage})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      bgcolor: !bgImage ? 'primary.main' : 'grey.900',
      boxShadow: '0 -15px 50px rgba(0,0,0,0.2)',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: bgImage 
            ? `linear-gradient(180deg, ${alpha('#000', 0.75)} 0%, ${alpha('#000', 0.88)} 40%, ${alpha('#000', 0.95)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)}, ${alpha(theme.palette.primary.main, 0.95)})`,
        zIndex: 1,
        backdropFilter: 'blur(3px)'
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'radial-gradient(circle at 20% 50%, rgba(255,107,107,0.1) 0%, transparent 50%)',
        zIndex: 1,
        pointerEvents: 'none'
      }
    }} component="footer">
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={{ xs: 4, sm: 5, md: 6 }}>
          {/* Brand & About Section */}
          <Grid item xs={12} md={4}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {logoUrl ? (
                  <Box sx={{
                    bgcolor: 'white',
                    p: 1,
                    borderRadius: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    width: { xs: 50, sm: 60 },
                    height: { xs: 50, sm: 60 }
                  }}>
                    <img 
                      src={logoUrl} 
                      alt="Logo" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain' 
                      }} 
                    />
                  </Box>
                ) : (
                  <Box sx={{
                    bgcolor: alpha('#fff', 0.1),
                    p: 1.5,
                    borderRadius: 3,
                    display: 'flex',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                  }}>
                    <StoreIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.primary.light }} />
                  </Box>
                )}
                <Typography variant="h4" sx={{ 
                  fontWeight: 900, 
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
                  background: `linear-gradient(135deg, #fff 0%, ${theme.palette.primary.light} 100%)`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.2
                }}>
                  {restaurantInfo?.Name || t('footer.storeName')}
                </Typography>
              </Box>

              {/* About Us Text */}
              {restaurantInfo?.AboutUs && (
                <Box sx={{ 
                  mb: 3,
                  p: 2.5,
                  bgcolor: alpha('#fff', 0.04),
                  borderRadius: 2,
                  borderLeft: `4px solid ${theme.palette.primary.main}`
                }}>
                  <Typography variant="body2" sx={{ 
                    color: alpha('#fff', 0.8), 
                    lineHeight: 1.8, 
                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                  }}>
                    {restaurantInfo.AboutUs.length > 150 
                      ? `${restaurantInfo.AboutUs.substring(0, 150)}...` 
                      : restaurantInfo.AboutUs}
                  </Typography>
                  <Link 
                    href="/about-us" 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: 1,
                      color: theme.palette.primary.light,
                      fontSize: { xs: '0.8rem', sm: '0.85rem' },
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {t('footer.readMore', 'Read more')} <InfoIcon sx={{ fontSize: 16 }} />
                  </Link>
                </Box>
              )}

              {/* Social Media */}
              {socialLinks.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 2, 
                    color: alpha('#fff', 0.7),
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}>
                    {t('footer.followUs', 'Follow Us')}
                  </Typography>
                  <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }}>
                    {socialLinks.map((social, index) => (
                      <SocialButton 
                        key={index}
                        icon={social.icon} 
                        href={social.url}
                        name={social.name}
                        color={social.color}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Grid>
          
          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={4}>
            <SectionTitle icon={<KeyboardArrowRightIcon />}>
              {t('footer.quickLinks')}
            </SectionTitle>
            <Stack spacing={{ xs: 0.5, sm: 1 }}>
              <FooterLink href="/">{t('footer.home')}</FooterLink>
              <FooterLink href="/about-us">{t('account.aboutUs')}</FooterLink>
              <FooterLink href="/offers">{t('footer.offers')}</FooterLink>
              <FooterLink href="/support">{t('footer.contactUs')}</FooterLink>
              <FooterLink href={APP_DOWNLOAD_URL} icon={<GetAppIcon />}>
                {t('footer.downloadApp', 'Download App')}
              </FooterLink>
            </Stack>

            {/* Join Us Button */}
            <Box sx={{ mt: { xs: 3, sm: 4 } }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate('/join')}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  py: { xs: 1.5, sm: 1.8 },
                  px: 3,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  textTransform: 'none',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  border: `2px solid ${alpha('#fff', 0.15)}`,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.5)'
                  },
                  '&:active': {
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s'
                }}
              >
                {t('navigation.joinUs', 'Join Us')}
              </Button>
            </Box>

            {/* QR Code */}
            <Box sx={{ 
              mt: { xs: 4, sm: 5 },
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Link
                href={APP_DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'block',
                  textDecoration: 'none',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'scale(1.08) rotate(-2deg)',
                  },
                  '&:active': {
                    transform: 'scale(0.95)'
                  }
                }}
              >
                <Box sx={{
                  position: 'relative',
                  p: { xs: 2.5, sm: 3 },
                  bgcolor: alpha('#fff', 0.12),
                  borderRadius: 4,
                  border: `3px solid ${alpha(theme.palette.primary.light, 0.2)}`,
                  boxShadow: `0 12px 32px ${alpha('#000', 0.4)}`,
                  backdropFilter: 'blur(10px)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    borderRadius: 4,
                    opacity: 0.3,
                    zIndex: -1,
                    animation: 'pulse 2s ease-in-out infinite'
                  }
                }}>
                  <img 
                    src="/QR-4D79F7FD04A2.png" 
                    alt="Download StoresYe App - QR Code"
                    style={{
                      width: isMobile ? '140px' : '160px',
                      height: isMobile ? '140px' : '160px',
                      display: 'block',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      padding: '8px'
                    }}
                  />
                  <Chip
                    icon={<GetAppIcon sx={{ fontSize: 18 }} />}
                    label={t('footer.scanToDownload', 'Scan to download')}
                    sx={{
                      mt: 2,
                      width: '100%',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '0.85rem' },
                      py: 2.5,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                      '& .MuiChip-icon': {
                        color: 'white',
                        fontSize: 20
                      },
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                </Box>
              </Link>
            </Box>
          </Grid>
          
          {/* Contact Info Section */}
          <Grid item xs={12} sm={6} md={4}>
            <SectionTitle icon={<ContactPhoneIcon />}>
              {t('footer.contactInfo')}
            </SectionTitle>
            <Stack spacing={{ xs: 1.5, sm: 2 }}>
              {/* Address */}
              {restaurantInfo?.Address && (
                <ContactItem 
                  icon={<LocationOnIcon />} 
                  label={t('footer.address', 'Address')}
                  text={restaurantInfo.Address} 
                  copyable
                />
              )}

              {/* Email */}
              {restaurantInfo?.Email && (
                <ContactItem 
                  icon={<EmailIcon />} 
                  label={t('footer.email', 'Email')}
                  text={restaurantInfo.Email}
                  href={`mailto:${restaurantInfo.Email}`}
                />
              )}

              {/* Main Phone */}
              {restaurantInfo?.PhoneNumber && (
                <ContactItem 
                  icon={<WhatsAppIcon />} 
                  label={t('footer.whatsapp', 'WhatsApp')}
                  text={restaurantInfo.PhoneNumber}
                  onClick={() => handleWhatsApp(restaurantInfo.PhoneNumber)}
                />
              )}

              {/* Additional Phones */}
              {additionalPhones.slice(0, 3).map((phone, index) => (
                <ContactItem 
                  key={index}
                  icon={getPhoneIcon(index)} 
                  label={phone.title}
                  text={phone.number}
                  onClick={() => handleWhatsApp(phone.number)}
                />
              ))}

              {/* Show more phones link if there are more than 3 */}
              {additionalPhones.length > 3 && (
                <Link
                  href="/support"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: theme.palette.primary.light,
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    fontWeight: 600,
                    textDecoration: 'none',
                    pl: 2,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  +{additionalPhones.length - 3} {t('footer.moreContacts', 'more contacts')}
                </Link>
              )}
            </Stack>
          </Grid>
        </Grid>
        
        {/* ✅ Mother Company Banner / Link Section */}
        <Box sx={{ mt: { xs: 5, sm: 6 }, mb: { xs: 2, sm: 3 } }}>
          <Box
            component="a"
            href={MOTHER_COMPANY_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: { xs: 2, sm: 2.5 },
              bgcolor: alpha('#fff', 0.06),
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.light, 0.25)}`,
              backdropFilter: 'blur(10px)',
              textDecoration: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: `0 8px 30px ${alpha('#000', 0.25)}`,
              '&:hover': {
                bgcolor: alpha('#fff', 0.1),
                borderColor: theme.palette.primary.light,
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                '& .company-arrow': {
                  transform: isRtl ? 'translateX(-6px)' : 'translateX(6px)',
                  color: theme.palette.primary.light
                }
              },
              '&:active': {
                transform: 'translateY(-1px)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
              <Box sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.3),
                p: { xs: 1.2, sm: 1.5 },
                borderRadius: 2.5,
                display: 'flex',
                color: theme.palette.primary.light,
                border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`
              }}>
                <BusinessIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{
                  color: alpha('#fff', 0.6),
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  fontWeight: 600
                }}>
                  {t('footer.poweredBy', 'Powered by')}
                </Typography>
                <Typography variant="subtitle1" sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', sm: '1.15rem' }
                }}>
                  CodeSpace | بيت البرمجيات الخاص بك 
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                label=" قم بزيارة موقعنا " 
                size="small"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  bgcolor: alpha(theme.palette.primary.main, 0.3),
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem'
                }}
              />
              <OpenInNewIcon 
                className="company-arrow"
                sx={{ 
                  color: alpha('#fff', 0.8), 
                  fontSize: { xs: 20, sm: 24 },
                  transition: 'all 0.3s'
                }} 
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ 
          borderColor: alpha('#fff', 0.12), 
          mt: { xs: 4, sm: 5 }, 
          mb: { xs: 3, sm: 4 } 
        }} />
        
        {/* Footer Bottom */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}>
          <Typography variant="body2" sx={{ 
            color: alpha('#fff', 0.5),
            fontSize: { xs: '0.75rem', sm: '0.85rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            &copy; {new Date().getFullYear()} {t('footer.allRightsReserved', 'All rights reserved.')} لـ {restaurantInfo?.Name || t('footer.copyright')} 
          </Typography>
          <Typography variant="caption" sx={{ 
            color: alpha('#fff', 0.4),
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            {t('footer.madeWith', 'Made with')} 
            <Box component="span" sx={{ color: '#ff6b6b', animation: 'heartbeat 1.5s ease-in-out infinite' }}>
              ❤️
            </Box>
            {t('footer.inYemen', 'in Yemen')}
          </Typography>
        </Box>
      </Container>

      {/* Snackbar for copy notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity="success" 
          variant="filled"
          sx={{ 
            width: '100%',
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            fontWeight: 600,
            boxShadow: `0 4px 12px ${alpha('#000', 0.3)}`
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* CSS for animations */}
      <style>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          10%, 30% { transform: scale(1.1); }
          20%, 40% { transform: scale(1); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </Box>
  );
}

export default Footer;
